import {
	ActionIcon,
	Avatar,
	Button,
	Divider,
	Group,
	Loader,
	Menu,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
	IconBuilding,
	IconCheck,
	IconFilter,
	IconLogin2,
	IconLogout,
	IconMenu2,
	IconSettings,
	IconShield,
} from "@tabler/icons-react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../api/client";
import hourglassMark from "../../assets/hourglass-mark.svg";
import { useSearch } from "../../contexts/SearchContext";
import { useViewState } from "../../contexts/ViewStateContext";
import {
	BetaAccessStatus,
	useGetCurrentUserAndOrganizationsQuery,
} from "../../graphql/generated/types.gen";
import { useActiveOrganization } from "../../hooks/useActiveOrganization";
import { useAuthCompat } from "../../hooks/useAuthCompat";
import { useIsMobile } from "../../hooks/useIsMobile";
import { onNodeImageChanged } from "../../utils/nodeImageEvents";
import { SearchBar } from "./SearchBar";
import { WhatsNew } from "./WhatsNew";

function useUserNodeImageUrl(
	organizationId: string | undefined,
	nodeId: string | null | undefined,
) {
	const [imageUrl, setImageUrl] = useState<string | null>(null);

	const fetchUrl = useCallback(() => {
		if (!organizationId || !nodeId) {
			setImageUrl(null);
			return;
		}
		apiClient
			.getNodeImageUrls(organizationId, [nodeId])
			.then(({ urls }) => setImageUrl(urls[nodeId] ?? null))
			.catch(() => setImageUrl(null));
	}, [organizationId, nodeId]);

	useEffect(() => {
		fetchUrl();
	}, [fetchUrl]);

	// Re-fetch when the user's node image changes anywhere in the app
	useEffect(() => {
		if (!nodeId) return;
		return onNodeImageChanged((changedNodeId, removed) => {
			if (changedNodeId === nodeId) {
				if (removed) {
					setImageUrl(null);
				} else {
					fetchUrl();
				}
			}
		});
	}, [nodeId, fetchUrl]);

	return imageUrl;
}

interface UserDropdownProps {
	isMobile?: boolean;
}

function UserDropdown({ isMobile }: UserDropdownProps) {
	const navigate = useNavigate();
	const openSettings = useCallback(() => {
		navigate({ to: "/settings" });
	}, [navigate]);
	const { user, signOut, signIn, signUp } = useAuthCompat();
	const { data, loading } = useGetCurrentUserAndOrganizationsQuery({
		skip: !user,
	});
	const {
		activeOrganization,
		isOrganizationActive,
		allOrganizations,
		setActiveOrganization,
	} = useActiveOrganization();
	const userImageUrl = useUserNodeImageUrl(
		activeOrganization?.id,
		activeOrganization?.nodeId,
	);

	const handleLogout = () => {
		signOut();
	};

	if (loading) {
		return (
			<div className="flex justify-center">
				<Loader size="sm" />
			</div>
		);
	}

	if (!user || !data?.currentUser) {
		return (
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={() => signUp()}
					className="px-4 py-2 flex items-center gap-2 text-sm font-medium cursor-pointer"
					style={{
						background: "transparent",
						color: "var(--ink)",
						border: "1px solid var(--hairline-strong)",
					}}
				>
					<IconLogin2 size={16} />
					Sign Up
				</button>
				<span className="text-sm" style={{ color: "var(--ink-secondary)" }}>
					or
				</span>
				<button
					type="button"
					onClick={() => signIn()}
					className="btn-gold px-4 py-2 flex items-center gap-2 text-sm"
					style={{ minHeight: 0 }}
				>
					<IconLogin2 size={16} />
					Login
				</button>
			</div>
		);
	}

	return (
		<Menu>
			<Menu.Target>
				{isMobile ? (
					<ActionIcon variant="subtle" size="lg" aria-label="User menu">
						<Avatar size="sm" radius="xl" src={userImageUrl}>
							{data.currentUser.displayName?.[0]?.toUpperCase()}
						</Avatar>
					</ActionIcon>
				) : (
					<Button
						variant="subtle"
						leftSection={
							<Avatar size="sm" radius="xl" src={userImageUrl}>
								{data.currentUser.displayName?.[0]?.toUpperCase()}
							</Avatar>
						}
					>
						<Text size="sm" truncate>
							{data.currentUser.displayName}
						</Text>
					</Button>
				)}
			</Menu.Target>
			<Menu.Dropdown>
				<Menu.Label>
					Organizations
					{activeOrganization && (
						<Text size="xs" c="dimmed" ml="auto">
							Active: {activeOrganization.name}
						</Text>
					)}
				</Menu.Label>
				{allOrganizations.map((org) => (
					<Menu.Item
						key={org.id}
						leftSection={<IconBuilding size={14} />}
						rightSection={
							isOrganizationActive(org.id) ? (
								<IconCheck size={14} color="green" />
							) : null
						}
						onClick={() => {
							if (!isOrganizationActive(org.id)) {
								// Reload so the next screen is that organization's own snapshot.
								setActiveOrganization(org.id);
								window.location.reload();
							}
						}}
					>
						<div>
							<Text size="sm" fw={isOrganizationActive(org.id) ? 500 : 400}>
								{org.name}
							</Text>
							<Text size="xs" c="dimmed">
								{org.role}
							</Text>
						</div>
					</Menu.Item>
				))}
				<Divider />
				<Menu.Item
					leftSection={<IconSettings size={14} />}
					onClick={openSettings}
				>
					Settings
				</Menu.Item>
				{data.currentUser.isEmployee && (
					<Menu.Item
						leftSection={<IconShield size={14} />}
						component="a"
						href="/admin"
					>
						Admin
					</Menu.Item>
				)}
				<Divider />
				<Menu.Item
					leftSection={
						<Avatar size={14} radius="xl" src={userImageUrl}>
							{data.currentUser.displayName?.[0]?.toUpperCase()}
						</Avatar>
					}
					onClick={openSettings}
				>
					<Text size="sm" fw={500}>
						{data.currentUser.displayName}
					</Text>
				</Menu.Item>
				<Menu.Item
					leftSection={<IconLogout size={14} />}
					onClick={handleLogout}
					color="red"
				>
					Logout
				</Menu.Item>
			</Menu.Dropdown>
		</Menu>
	);
}

export function Header() {
	const { user } = useAuthCompat();
	const { data: userData } = useGetCurrentUserAndOrganizationsQuery({
		skip: !user,
	});
	const { activeOrganization } = useActiveOrganization();
	const { onNodeSelected } = useSearch();
	const isMobile = useIsMobile();
	// Narrower breakpoint for stacking the search bar below the header
	const isNarrowScreen = useMediaQuery("(max-width: 640px)") ?? false;
	const {
		mobileSidebarOpen,
		setMobileSidebarOpen,
		mobileContextPanelOpen,
		setMobileContextPanelOpen,
		filters,
	} = useViewState();

	// Check if any filter is active
	const hasActiveFilters =
		filters.nodeTypes.length > 0 ||
		filters.dateRange?.start !== null ||
		filters.dateRange?.end !== null ||
		Object.keys(filters.customFilters).length > 0;

	const isAuthenticated = user && userData?.currentUser;
	const isWaitlisted =
		userData?.currentUser?.betaAccessStatus === BetaAccessStatus.Waitlist;
	const hasFullAccess = isAuthenticated && !isWaitlisted;

	const routerState = useRouterState();
	const { pathname } = routerState.location;
	const isMinimalPage =
		["/organization-setup", "/waitlist"].includes(pathname) ||
		pathname.startsWith("/invite/");

	if (isMobile) {
		return (
			<header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 w-full h-full">
				<Stack gap={0} className="h-full">
					{/* Search bar centered across full screen on wider mobile */}
					{hasFullAccess && !isMinimalPage && !isNarrowScreen && (
						<div className="absolute left-0 right-0 top-0 h-[52px] flex items-center justify-center pointer-events-none px-3">
							<div className="w-full max-w-md pointer-events-auto">
								<SearchBar hideShortcut />
							</div>
						</div>
					)}

					<div className="px-3 py-2 relative z-10">
						<div className="flex items-center justify-between">
							<Group gap="xs" wrap="nowrap">
								{isAuthenticated && !isMinimalPage && (
									<ActionIcon
										variant="subtle"
										size="lg"
										onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
										aria-label="Open navigation menu"
									>
										<IconMenu2 size={24} />
									</ActionIcon>
								)}
								<Link
									to="/"
									style={{ textDecoration: "none", color: "inherit" }}
								>
									<img
										src={hourglassMark}
										alt="Relatance"
										width="24"
										height="32"
									/>
								</Link>
							</Group>

							<Group gap="xs" wrap="nowrap">
								{hasFullAccess && !isMinimalPage && (
									<ActionIcon
										variant={hasActiveFilters ? "filled" : "subtle"}
										color={hasActiveFilters ? "blue" : undefined}
										size="lg"
										onClick={() =>
											setMobileContextPanelOpen(!mobileContextPanelOpen)
										}
										aria-label="Toggle filters"
									>
										<IconFilter size={20} />
									</ActionIcon>
								)}
								<WhatsNew />
								<UserDropdown isMobile />
							</Group>
						</div>
					</div>

					{/* Search bar stacked full-width on narrow screens */}
					{hasFullAccess && !isMinimalPage && isNarrowScreen && (
						<div className="px-3 pb-2">
							<SearchBar hideShortcut fullWidth />
						</div>
					)}
				</Stack>
			</header>
		);
	}

	return (
		<header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 w-full h-full">
			<div className="max-w-[1400px] mx-auto px-6 h-full">
				<Group justify="space-between" align="center" h="100%">
					<Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
						<Group gap="sm" align="center">
							<img src={hourglassMark} alt="Relatance" width="24" height="32" />
							<div>
								<Title order={1} size="h2" style={{ cursor: "pointer" }}>
									Relatance
								</Title>
								{activeOrganization && (
									<Text size="xs" c="dimmed" mt={-4}>
										{activeOrganization.name}
									</Text>
								)}
							</div>
						</Group>
					</Link>

					{hasFullAccess && !isMinimalPage && (
						<div className="flex-1 max-w-md mx-8">
							<SearchBar onNodeSelected={onNodeSelected} />
						</div>
					)}

					<Group gap="xs">
						<WhatsNew />
						<UserDropdown />
					</Group>
				</Group>
			</div>
		</header>
	);
}
